import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  CounterQuestionConfig,
  CounterType,
  DateTypeFilter,
  getFullName,
  RoutineType,
  SortDirectionEnum,
  useCustomQuery,
  useFilteredWorkspaceRoutines,
  useFindByQuery,
  WorkflowRoutineConfig,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useDataPlateColumns } from '../../accounts/components/dataExport/useDataPlateColumns';
import {
  extractIndividualValues,
  formatResponseValue,
  getResponseKey,
  MULTI_VALUE_KEYS,
  sanitizeCsvString,
} from './routineExportFormat';

type Props = {
  label: string;
  routineIds: string[];
  workspaceId: string;
  dateFilter?: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  };
  hideButton?: boolean;
  /**
   * When true, each individual answer value (barcode scan, image, multi-/single-
   * select option) is exported as its own row instead of being consolidated into
   * one cell. Only affects Workflow routines; single-value routine types
   * (Counter/Status/QuickConfirm) are unchanged.
   */
  splitToRows?: boolean;
  /**
   * When true, appends the asset's description & details plus the latest
   * submission of every one-time ("data plate") routine on the asset.
   */
  addAllAssetInfo?: boolean;
};

type CSVData = {
  headers: any[];
  rows: any[];
  filename: string;
};

export const DownloadRoutine = forwardRef<
  { triggerDownload: () => void },
  Props
>((props, ref) => {
  const {
    label,
    routineIds,
    workspaceId,
    dateFilter,
    hideButton = false,
    splitToRows = false,
    addAllAssetInfo = false,
  } = props || {};
  const { t } = useTranslation();
  const csvRefs = useRef<(any | null)[]>([]);
  const [csvDataList, setCsvDataList] = useState<CSVData[]>([]);
  const [doExport, toggleExport] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentDownloadIndex, setCurrentDownloadIndex] = useState(0);
  const [uniqueVersionIds, setUniqueVersionIds] = useState<string[]>([]);

  // Expose download function via ref
  useImperativeHandle(ref, () => ({
    triggerDownload: onDownload,
  }));

  const routines = useFilteredWorkspaceRoutines(
    { id: { in: routineIds } },
    workspaceId
  );

  const dataPlate = useDataPlateColumns(workspaceId, addAllAssetInfo && doExport);
  const getDateFilter = () => {
    if (!dateFilter || dateFilter.dateType === DateTypeFilter.ALL) {
      return {};
    }

    if (dateFilter.dateType === DateTypeFilter.CUSTOMDATE) {
      const filter: any = {};
      if (dateFilter.startDate) {
        // Send actual UTC time
        const startDate = new Date(dateFilter.startDate);
        filter.ge = startDate.toISOString();
      }
      if (dateFilter.endDate) {
        // Send actual UTC time at end of day
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999);
        filter.le = endDate.toISOString();
      }
      return filter;
    }

    const now = new Date();
    let startDate: Date;
    const endDate: Date = new Date(now);

    switch (dateFilter.dateType) {
      case DateTypeFilter.TODAY:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDate.setHours(0, 0, 0, 0);
        break;

      case DateTypeFilter.THISWEEK:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;

      case DateTypeFilter.THISMONTH:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 29);
        startDate.setHours(0, 0, 0, 0);
        break;

      default:
        return {};
    }
    endDate.setHours(23, 59, 59, 999);

    return {
      ge: startDate.toISOString(),
      le: endDate.toISOString(),
    };
  };

  const dateFilterRange = getDateFilter();
  const hasDateFilter = Object.keys(dateFilterRange).length > 0;

  const { data } = useCustomQuery({
    query: GET_ROUTINE_DATA,
    cacheKey: ['exportRoutineData', routineIds, dateFilter],
    variables: {
      filter: {
        routine: { in: routineIds },
        ...(hasDateFilter && {
          or: [
            { adjustCreationDate: dateFilterRange },
            { createdAt: dateFilterRange },
          ],
        }),
        isDraft: { ne: true },
      },
      orderBy: { field: 'createdAt', order: SortDirectionEnum.Asc },
    },
    options: {
      enabled: doExport,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  // Extract unique version IDs from the data
  const [versionIdsExtracted, setVersionIdsExtracted] = useState(false);

  useEffect(() => {
    if (!data) {
      setVersionIdsExtracted(false);
      return;
    }

    const versionIds = new Set<string>();
    data.findAssetRoutines.items.forEach((item: any) => {
      if (item.routineVersion?.id) {
        versionIds.add(item.routineVersion.id);
      }
    });

    const uniqueIds = Array.from(versionIds);
    setUniqueVersionIds(uniqueIds);
    setVersionIdsExtracted(true);
  }, [data]);

  const { data: versionData, isLoading: isVersionsLoading } = useFindByQuery({
    query: GET_ROUTINE_VERSIONS,
    modelName: 'workspaceRoutineVersion',
    variables: {
      filter: {
        id: { in: uniqueVersionIds },
      },
    },
    options: {
      enabled: uniqueVersionIds.length > 0,
    },
  });

  const versionConfigsMap = useMemo(() => {
    const map = new Map<string, any>();
    if (versionData?.items) {
      versionData.items.forEach((version: any) => {
        map.set(version.id, version);
      });
    }
    return map;
  }, [versionData]);

  const baseHeaders = useMemo(
    () => [
      { label: 'Asset', key: 'asset' },
      { label: 'Created By', key: 'createdBy' },
      { label: 'Adjusted Date', key: 'adjustedDate' },
      { label: 'Created Date', key: 'createdDate' },
      { label: 'Created Time', key: 'createdTime' },
      { label: 'Creation UTC', key: 'creationUtc' },
      { label: 'Groups', key: 'groups' },
      { label: 'Is Edit', key: 'isEdit' },
    ],
    []
  );

  // Extra asset columns inserted after the base columns when "Add all asset
  // info" is on. The data-plate columns are appended at the far right and are
  // already empty when the option is off (the hook returns no headers).
  const assetInfoHeaders = useMemo(
    () =>
      addAllAssetInfo
        ? [
            { label: 'Description', key: 'description' },
            { label: 'Details', key: 'details' },
          ]
        : [],
    [addAllAssetInfo]
  );

  const createCounterColumns = useCallback(
    (baseLabel: string, questionId: string) => {
      return [
        { label: `${baseLabel} - Starting Value`, key: `${questionId}_start` },
        { label: `${baseLabel} - Delta`, key: `${questionId}_delta` },
        { label: `${baseLabel} - Ending Value`, key: questionId },
      ];
    },
    []
  );

  const formatCounterData = useCallback((response: any) => {
    const count = response.count ?? 0;
    const userCount = response.userCount ?? 0;
    const startingValue = count - userCount;

    return {
      [`${response.questionId}_start`]: startingValue.toString(),
      [`${response.questionId}_delta`]: userCount.toString(),
      [response.questionId]: count.toString(),
    };
  }, []);

  const onDownload = () => {
    if (csvDataList?.length > 0) {
      // user already clicked on icon 1x, so just download it
      if (csvDataList.length === 1) {
        // Single file download
        setTimeout(() => {
          csvRefs.current[0]?.link?.click();
        }, 50);
      } else {
        // Multiple files download - download them sequentially
        setCurrentDownloadIndex(0);
        downloadNextFile();
      }
    } else {
      toggleExport(true);
      // setLabel('exporting');
    }
  };

  const downloadNextFile = useCallback(() => {
    if (currentDownloadIndex >= csvDataList.length) {
      // All files downloaded
      toggleExport(false);
      setCurrentDownloadIndex(0);
      return;
    }

    setTimeout(() => {
      csvRefs.current[currentDownloadIndex]?.link?.click();
      setCurrentDownloadIndex((prev) => prev + 1);
    }, 100);
  }, [currentDownloadIndex, csvDataList.length]);

  // Trigger next download when currentDownloadIndex changes
  useEffect(() => {
    if (
      doExport &&
      csvDataList.length > 1 &&
      currentDownloadIndex > 0 &&
      currentDownloadIndex < csvDataList.length
    ) {
      downloadNextFile();
    }
  }, [currentDownloadIndex, doExport, csvDataList.length, downloadNextFile]);

  const getBaseRowData = useCallback((item: any) => {
    const createdDate = item.createdAt ? new Date(item.createdAt) : null;
    const adjustedDate = item.adjustCreationDate
      ? new Date(item.adjustCreationDate)
      : null;

    return {
      asset: sanitizeCsvString(item.asset.name),
      description: sanitizeCsvString(item.asset.description ?? ''),
      details: sanitizeCsvString(item.asset.details ?? ''),
      createdBy: sanitizeCsvString(getFullName(item.createdBy.name)),
      adjustedDate: adjustedDate ? adjustedDate.toLocaleDateString() : '',
      createdDate: createdDate ? createdDate.toLocaleDateString() : '',
      createdTime: createdDate ? createdDate.toLocaleTimeString() : '',
      creationUtc: createdDate ? createdDate.toISOString() : '',
      groups:
        item.workspaceAsset.groups?.length > 0
          ? item.workspaceAsset.groups
              .filter((g: any) => g && g.name)
              .map((g: any) => g.name)
              .join(', ')
          : '',
      isEdit: item.data?.auditLog?.auditLog?.length > 0 ? 'Yes' : '',
    };
  }, []);

  const createWorkflowData = useCallback(
    (items: any[], workspaceRoutine: WorkspaceRoutine): CSVData => {
      // Collect all unique questions from all versions in the data
      const allQuestionsMap = new Map<
        string,
        { id: string; instructions: string; order: number }
      >();
      const multiColumnCounterIds = new Set<string>();

      items.forEach((item) => {
        const versionId = item.routineVersion?.id;
        const versionData = versionId ? versionConfigsMap.get(versionId) : null;

        if (versionData?.config?.pages) {
          const versionConfig = versionData.config as WorkflowRoutineConfig;
          versionConfig.pages.forEach((page) => {
            page.questions?.forEach((question) => {
              allQuestionsMap.set(question.id, {
                id: question.id,
                instructions: question.instructions || '',
                order: question.order,
              });

              if (question.type === 'COUNTER') {
                const counterType = (question as CounterQuestionConfig)
                  .counterType;
                if (
                  counterType === CounterType.Cumulative ||
                  counterType === CounterType.Tally
                ) {
                  multiColumnCounterIds.add(question.id);
                }
              }
            });
          });
        }

        // Check responses for nested counter routines
        item.data.responses?.forEach((response: any) => {
          if (
            response.counterType === CounterType.Cumulative ||
            response.counterType === CounterType.Tally
          ) {
            multiColumnCounterIds.add(response.questionId);
          }
        });
      });

      // Sort questions by order to maintain some consistency
      const allQuestions = Array.from(allQuestionsMap.values()).sort(
        (a, b) => a.order - b.order
      );

      // In split mode, multi-value answers (barcode scans, images, multi-selects)
      // are spread across one column per value instead of being comma-joined
      // into a single cell. Compute how many columns each such question needs —
      // the max value count across all submissions — and only split when there
      // is actually more than one value to break out.
      const splitColumnCounts = new Map<string, number>();
      if (splitToRows) {
        items.forEach((item) => {
          item.data.responses?.forEach((response: any) => {
            const qid = response.questionId;
            if (multiColumnCounterIds.has(qid)) return;
            const valueKey = getResponseKey(response);
            if (!valueKey || !MULTI_VALUE_KEYS.has(valueKey)) return;
            const count = extractIndividualValues(response, valueKey).length;
            splitColumnCounts.set(
              qid,
              Math.max(splitColumnCounts.get(qid) ?? 0, count)
            );
          });
        });
        // Questions that never had more than one value stay single-column.
        for (const [qid, count] of splitColumnCounts) {
          if (count <= 1) splitColumnCounts.delete(qid);
        }
      }

      const splitColumnKey = (questionId: string, index: number) =>
        `${questionId}__${index}`;

      const headers = [...baseHeaders, ...assetInfoHeaders];
      allQuestions.forEach((question) => {
        if (multiColumnCounterIds.has(question.id)) {
          headers.push(
            ...createCounterColumns(question.instructions, question.id)
          );
        } else if (splitColumnCounts.has(question.id)) {
          const count = splitColumnCounts.get(question.id) as number;
          for (let i = 0; i < count; i++) {
            headers.push({
              label: `${question.instructions} ${i + 1}`,
              key: splitColumnKey(question.id, i),
            });
          }
        } else {
          headers.push({ label: question.instructions, key: question.id });
        }
      });
      headers.push(...dataPlate.dataPlateHeaders);

      const rows = items.map((item) => {
        const baseRowData = getBaseRowData(item);
        const row: any = {
          ...baseRowData,
          ...(dataPlate.valuesByAssetId[item.asset.id] ?? {}),
        };

        // Map responses to question columns
        item.data.responses?.forEach((response: any) => {
          const qid = response.questionId;

          if (multiColumnCounterIds.has(qid)) {
            Object.assign(row, formatCounterData(response));
            return;
          }

          const valueKey = getResponseKey(response);

          if (splitColumnCounts.has(qid)) {
            // Spread each individual value into its own cell.
            extractIndividualValues(response, valueKey).forEach(
              (value, index) => {
                row[splitColumnKey(qid, index)] = value;
              }
            );
            return;
          }

          row[qid] = formatResponseValue(response, valueKey);
        });

        return row;
      });

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [
      baseHeaders,
      assetInfoHeaders,
      dataPlate,
      getBaseRowData,
      formatCounterData,
      createCounterColumns,
      versionConfigsMap,
      splitToRows,
    ]
  );

  const createNonWorkflowData = useCallback(
    (items: any[], workspaceRoutine: WorkspaceRoutine): CSVData => {
      const config = workspaceRoutine.config as any;
      const isMultiColumn =
        workspaceRoutine.type === RoutineType.Counter &&
        (config?.counterType === CounterType.Cumulative ||
          config?.counterType === CounterType.Tally);

      const headers = [...baseHeaders, ...assetInfoHeaders];

      if (isMultiColumn) {
        headers.push(
          {
            label: `${workspaceRoutine.config.title} - Starting Value`,
            key: 'startingValue',
          },
          { label: `${workspaceRoutine.config.title} - Delta`, key: 'delta' },
          {
            label: `${workspaceRoutine.config.title} - Ending Value`,
            key: 'data',
          }
        );
      } else {
        headers.push({ label: workspaceRoutine.config.title, key: 'data' });
      }
      headers.push(...dataPlate.dataPlateHeaders);

      const rows = items.map((item) => {
        const baseRowData = getBaseRowData(item);
        let data = '';

        switch (workspaceRoutine.type) {
          case RoutineType.Status:
            // Status routine: use status value, or openEndedResponse if user typed custom value
            if (
              item.data.status === 'openEndedResponse' &&
              item.data.openEndedResponse
            ) {
              data = item.data.openEndedResponse;
            } else {
              data = item.data.status || '';
            }
            break;
          case RoutineType.Counter:
            // Counter routine: use count as the value (for Simple it's the only value, for Cumulative/Tally it's the ending value)
            data = item.data.count?.toString() ?? '';
            break;
          case RoutineType.QuickConfirm:
            // QuickConfirm routine: use confirmedValue
            data = item.data.confirmedValue || '';
            break;
          default:
            // Fallback for any other types - grab first value from data object
            const values = Object.values(item.data).filter(
              (v) => v !== undefined && v !== null
            );
            data = values.length > 0 ? String(values[0]) : '';
        }

        const row: any = {
          ...baseRowData,
          ...(dataPlate.valuesByAssetId[item.asset.id] ?? {}),
          data,
        };

        if (isMultiColumn) {
          const count = item.data.count ?? 0;
          const userCount = item.data.userCount ?? 0;
          const startingValue = count - userCount;

          row.startingValue = startingValue.toString();
          row.delta = userCount.toString();
        }

        return row;
      });

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [baseHeaders, assetInfoHeaders, dataPlate, getBaseRowData]
  );

  useEffect(() => {
    if (!data || !versionIdsExtracted) {
      return;
    }

    if (
      uniqueVersionIds.length > 0 &&
      (isVersionsLoading || versionConfigsMap.size === 0)
    ) {
      return;
    }

    // Hold off building the CSVs until the data-plate columns are ready, so the
    // download isn't triggered with empty "all asset info" columns.
    if (addAllAssetInfo && dataPlate.isLoading) {
      return;
    }

    const newCsvDataList: CSVData[] = [];

    const supersededIds = new Set(
      data.findAssetRoutines.items
        .map((item: any) => item.revisionOf?.id)
        .filter(Boolean)
    );

    const { ge, le } = dateFilterRange as { ge?: string; le?: string };
    const rangeStart = ge ? new Date(ge) : undefined;
    const rangeEnd = le ? new Date(le) : undefined;
    const getEffectiveDate = (item: any) =>
      new Date(item.adjustCreationDate ?? item.createdAt);

    routines.forEach((routine) => {
      const routineItems = data.findAssetRoutines.items
        .filter((item: any) => {
          if (item.routine.id !== routine.id || supersededIds.has(item.id))
            return false;
          if (!hasDateFilter) return true;
          const effectiveDate = getEffectiveDate(item);
          if (rangeStart && effectiveDate < rangeStart) return false;
          if (rangeEnd && effectiveDate > rangeEnd) return false;
          return true;
        })
        .sort(
          (a: any, b: any) =>
            getEffectiveDate(a).getTime() - getEffectiveDate(b).getTime()
        );

      if (routineItems.length > 0) {
        if (routine.type === RoutineType.Workflow) {
          newCsvDataList.push(createWorkflowData(routineItems, routine));
        } else {
          newCsvDataList.push(createNonWorkflowData(routineItems, routine));
        }
      }
    });

    setCsvDataList(newCsvDataList);
  }, [
    data,
    dateFilter,
    routines,
    createWorkflowData,
    createNonWorkflowData,
    uniqueVersionIds,
    versionConfigsMap,
    isVersionsLoading,
    versionIdsExtracted,
    addAllAssetInfo,
    dataPlate,
  ]);

  // Separate effect for triggering download when doExport changes
  useEffect(() => {
    if (doExport && csvDataList.length > 0) {
      if (csvDataList.length === 1) {
        // Single file download
        setTimeout(() => {
          csvRefs.current[0]?.link?.click();
          toggleExport(false);
          setCurrentDownloadIndex(0);
        }, 50);
      } else {
        // Multiple files download - download them sequentially
        setCurrentDownloadIndex(0);
        downloadNextFile();
      }
    }
  }, [doExport, csvDataList.length]);

  return (
    <>
      {!hideButton && (
        <Pressable
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          style={({ pressed }) => [
            styles.customButton,
            styles.buttonStyle,
            isHovered && styles.hovered,
            pressed && styles.pressed,
          ]}
          onPress={onDownload}
          disabled={doExport}
        >
          <View style={{ flexDirection: 'row' }}>
            <MaterialCommunityIcons
              name="download"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            <Text style={styles.label}>{label}</Text>
          </View>
        </Pressable>
      )}

      {/* Render CSV links for each routine */}
      {csvDataList.map((csvData, index) => (
        <CSVLink
          key={index}
          style={{ display: 'none' }}
          ref={(el: any) => (csvRefs.current[index] = el)}
          data={csvData.rows}
          headers={csvData.headers}
          filename={csvData.filename}
        />
      ))}
    </>
  );
});

const styles = StyleSheet.create({
  icon: {
    marginRight: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: -0.25,
  },
  customButton: {
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderRadius: 8,
  },
  hovered: {
    backgroundColor: Colors.primaryGrayHeader,
  },
  pressed: {
    backgroundColor: Colors.blackWithAlpha,
  },
  buttonStyle: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 128,
    height: 32,
    backgroundColor: Colors.white,
    borderRadius: 16,
  },
});

const GET_ROUTINE_DATA = `
  query FindAssetRoutines($filter: AssetRoutineFilter, $orderBy: OrderByInput) {
    findAssetRoutines(filter: $filter, orderBy: $orderBy) {
      items {
        routine {
          id
        }
        routineVersion {
          id
        }
        asset {
          id
          name
          description
          details
        }
        workspaceAsset {
          groups {
            name
          }
        }
        createdAt
        adjustCreationDate
        createdBy {
          name {
            first
            last
          }
        }
        id
        revisionOf {
          id
        }
        data
      }
    }
  }
`;

const GET_ROUTINE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        id
        type
        config
      }
    }
  }
`;
